/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Dynamic SEO landing pages: /services/:serviceSlug[/:citySlug]
 *
 * Purpose: target long-tail city + service queries like
 *   "biomedical waste collection Trivandrum"
 *   "diaper waste disposal Kochi"
 *   "expired medicine disposal Kerala"
 * with dedicated URLs, unique meta, JSON-LD Service + LocalBusiness +
 * FAQPage, and a coherent conversion path (WhatsApp / Book service).
 */
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, WhatsappLogo, MapPin, ShieldCheck, Recycle, CheckCircle,
  Phone, Clock, Package, Truck,
} from "@phosphor-icons/react";
import { MarketingNav, MarketingFooter, Eyebrow, BTN_GREEN } from "./_shared";
import SEO, {
  PROD_ORIGIN, CITY_BRANCHES, organizationSchema, localBusinessSchema,
  serviceSchema, faqSchema,
} from "../../lib/seo";

// ============================================================
// Service catalogue — the primary keyword targets. Each service has
// its own SEO copy, FAQs and dedicated pillar page.
// ============================================================
export const SERVICES = {
  "biomedical-waste-collection": {
    name: "Biomedical Waste Collection",
    short: "biomedical waste collection",
    tagline: "CPCB & BMW-2016 compliant. Homes, clinics, labs.",
    heroTitle: "Biomedical waste collection service",
    metaKeywords:
      "biomedical waste collection, biomedical waste pickup, BMW disposal, biomedical waste management, CPCB compliant waste service",
    intro:
      "Rescigo runs Kerala's largest doorstep biomedical waste collection network for households, clinics, dental offices, diagnostic labs and small hospitals. Each pickup is logged, geo-tagged and disposed at a CPCB-authorised common bio-medical waste treatment facility (CBMWTF).",
    features: [
      "Colour-coded segregation (yellow / red / blue / white) as per BMW 2016",
      "Weekly or on-demand pickup, GPS-tracked pickup vehicle",
      "Digital consignment note & annual compliance certificate",
      "Serving 5,000+ households and 100+ healthcare institutions",
    ],
    icon: Recycle,
    faqs: [
      { q: "Is biomedical waste collection legally required at home?",
        a: "Yes — under CPCB & Kerala State Pollution Control Board rules, any household or facility generating BMW (used syringes, expired medicines, incontinence pads with medical content) must dispose it through an authorised collector. Rescigo handles the full compliance chain on your behalf." },
      { q: "How often does Rescigo collect biomedical waste?",
        a: "Home subscribers get a weekly pickup by default. Clinics and labs can opt for 2×/week or daily pickup based on volume." },
      { q: "Do you provide colour-coded bags & sharps containers?",
        a: "Yes — every subscription includes segregation bags (yellow, red, blue, white puncture-proof) and a sharps container refreshed at each pickup." },
      { q: "What happens to the waste after pickup?",
        a: "Waste is transported in dedicated BMW vehicles to a CPCB-authorised CBMWTF for autoclaving, shredding, deep-burial or incineration depending on category." },
    ],
    price: "Starts at ₹99/month for households",
  },
  "diaper-waste-disposal": {
    name: "Diaper Waste Disposal",
    short: "diaper waste disposal",
    tagline: "Baby & adult diapers — hygienic, doorstep pickup.",
    heroTitle: "Diaper waste disposal service",
    metaKeywords:
      "diaper waste disposal, adult diaper collection, baby diaper pickup, incontinence waste management",
    intro:
      "Used diapers (baby or adult) are classified as biomedical waste under Indian rules when they contain body fluids. Rescigo picks up sealed diaper waste in colour-coded bags on a schedule that fits your family — no more overflowing bins, no more smell, and full peace of mind on compliance.",
    features: [
      "Odour-locked pickup bags provided free of cost",
      "Weekly or twice-weekly frequency",
      "Discreet, uniformed collection staff",
      "Diapers dispatched to authorised incineration only",
    ],
    icon: Package,
    faqs: [
      { q: "Are diapers considered biomedical waste in India?",
        a: "Yes — soiled diapers containing body fluids fall under BMW category (yellow bag) per BMW 2016. Municipal solid-waste bins are not authorised for this category." },
      { q: "Do I need a separate bin at home?",
        a: "Rescigo provides a sealed diaper bag (odour-locked) at every pickup — you just tie it up and hand it over. No dedicated bin needed." },
      { q: "Is the service available in my city?",
        a: "Yes across Trivandrum, Kochi and Kottayam. Other Kerala LSGDs are added continuously — check availability at /register." },
    ],
    price: "Starts at ₹149/month for weekly pickup",
  },
  "sanitary-waste-disposal": {
    name: "Sanitary Waste Disposal",
    short: "sanitary napkin waste disposal",
    tagline: "Sanitary napkins & tampons collected discreetly.",
    heroTitle: "Sanitary napkin waste disposal",
    metaKeywords:
      "sanitary waste disposal, sanitary napkin disposal, menstrual waste collection, women's hygiene waste",
    intro:
      "Sanitary napkins and tampons are BMW yellow-bag waste and must be incinerated, not mixed with municipal garbage. Rescigo's sanitary waste programme picks up sealed napkin bags weekly, with 100% discreet handling.",
    features: [
      "Sealed odour-lock disposal bags at every pickup",
      "Weekly collection · fixed pickup time",
      "Disposed via CPCB-authorised incineration",
      "Available for homes, offices, girls' hostels & schools",
    ],
    icon: Package,
    faqs: [
      { q: "Why can't I throw sanitary pads in regular garbage?",
        a: "Under BMW 2016 rules, sanitary napkins with menstrual content are yellow-category BMW and must be incinerated, not landfilled with domestic garbage." },
      { q: "Does Rescigo serve offices and hostels?",
        a: "Yes — we run institutional plans for corporates, girls' hostels and residential schools with daily or twice-daily pickup." },
    ],
    price: "Starts at ₹99/month for homes",
  },
  "home-biomedical-waste-collection": {
    name: "Home Biomedical Waste Collection",
    short: "home biomedical waste collection",
    tagline: "For households with sick or elderly patients at home.",
    heroTitle: "Home biomedical waste collection",
    metaKeywords:
      "home biomedical waste collection, doorstep BMW pickup, household medical waste disposal, home healthcare waste",
    intro:
      "If you're caring for a family member at home — dialysis, diabetes injections, wound care, ostomy bags, catheters — the used medical items are biomedical waste and must be disposed through an authorised collector. Rescigo's Home BMW plan gives you compliant, doorstep pickup of all sharps, syringes and dressings.",
    features: [
      "Sharps container provided free",
      "Yellow + Red + Blue bags refreshed weekly",
      "Perfect for diabetes, dialysis, elderly home-care",
      "Digital pickup receipt after every visit",
    ],
    icon: Truck,
    faqs: [
      { q: "What items go into the home BMW bag?",
        a: "Used syringes and needles (sharps container), IV sets, gloves, wound dressings, catheters, ostomy bags, expired medicines and diagnostic-strip waste." },
      { q: "How do I subscribe?",
        a: "Sign up at /register — pick 'Home BMW plan' and we'll assign a route within 24 hours. Your first pickup happens within a week." },
    ],
    price: "Starts at ₹199/month",
  },
  "expired-medicine-disposal": {
    name: "Expired Medicine Disposal",
    short: "expired medicine disposal",
    tagline: "Safely dispose expired tablets, syrups, ointments.",
    heroTitle: "Expired medicine disposal service",
    metaKeywords:
      "expired medicine disposal, expired drug pickup, unused medicine collection, pharmaceutical waste disposal",
    intro:
      "Expired or unused medicines are hazardous — flushed pills contaminate water; landfilled tablets get picked up by animals or children. Rescigo collects expired medication (tablets, syrups, ointments, insulin pens) and routes them to CPCB-authorised incineration under the BMW 2016 framework.",
    features: [
      "Pickup from home, clinic or pharmacy",
      "All dosage forms accepted — tablets, liquids, ointments, injections",
      "Digital certificate for each disposal batch",
      "Free monthly disposal drives with select LSGDs",
    ],
    icon: Recycle,
    faqs: [
      { q: "Can I mix expired medicines with regular BMW waste?",
        a: "Yes — expired medicines fall under the same BMW yellow-bag category. Rescigo's regular biomedical pickup accepts them at no extra cost." },
      { q: "What about controlled substances or narcotics?",
        a: "Controlled substances require Drug Controller intervention. Contact us and we'll co-ordinate the pickup with the required authorities." },
    ],
    price: "Free with any Rescigo BMW plan",
  },
};

// ============================================================
// Cities — dynamic content per Kerala district. Aliases power keyword
// coverage (e.g. "Trivandrum" AND "Thiruvananthapuram").
// ============================================================
export const CITIES = {
  "trivandrum": {
    name: "Trivandrum",
    aliases: "Thiruvananthapuram, TVM",
    region: "Kerala",
    intro:
      "Rescigo runs Kerala's largest doorstep biomedical waste collection network from its Trivandrum HQ. We serve 15+ wards across Kazhakoottam, Sreekaryam, Pattom, Kowdiar, Vazhuthacaud and beyond.",
    branch: CITY_BRANCHES.find(b => b.slug === "trivandrum"),
  },
  "kochi": {
    name: "Kochi",
    aliases: "Ernakulam, Cochin",
    region: "Kerala",
    intro:
      "Rescigo's Kochi branch covers Ernakulam district — Panampilly Nagar, Kaloor, Edapally, Kakkanad, Fort Kochi and neighbourhoods. Weekly and on-demand pickup available.",
    branch: CITY_BRANCHES.find(b => b.slug === "kochi"),
  },
  "kottayam": {
    name: "Kottayam",
    aliases: "",
    region: "Kerala",
    intro:
      "Rescigo's Kottayam branch serves the city and adjoining LSGDs (Changanassery, Ettumanoor) with weekly biomedical, diaper and sanitary waste collection.",
    branch: CITY_BRANCHES.find(b => b.slug === "kottayam"),
  },
  "kerala": {
    name: "Kerala",
    aliases: "",
    region: "Kerala",
    intro:
      "Rescigo operates across Kerala — Trivandrum, Kochi, Kottayam and 12+ cities and municipalities. Every pickup is CPCB & BMW-2016 compliant, geo-tagged and dispatched to a Kerala State Pollution Control Board-authorised treatment facility.",
    branch: null,
  },
  "india": {
    name: "India",
    aliases: "",
    region: "India",
    intro:
      "Rescigo's India-wide compliance framework helps hospitals, clinics and homes stay BMW 2016 & CPCB compliant. Doorstep collection is currently live across Kerala with rollout planned to Karnataka and Tamil Nadu.",
    branch: null,
  },
};

// ============================================================
// The landing page
// ============================================================
export default function ServiceLandingPage() {
  const { serviceSlug, citySlug } = useParams();
  const service = SERVICES[serviceSlug];
  const city = CITIES[citySlug || "kerala"];

  if (!service) return <Navigate to="/" replace/>;

  // ============================================================
  // Copy & SEO strings
  // ============================================================
  const cityLabel = city.name;
  const pathBase = `/services/${serviceSlug}${citySlug ? `/${citySlug}` : ""}`;
  const title = citySlug
    ? `${service.name} in ${cityLabel}${city.aliases ? ` (${city.aliases})` : ""}`
    : `${service.name} · Kerala & India`;
  const description = citySlug
    ? `${service.name} in ${cityLabel} by Rescigo Health. CPCB & BMW-2016 compliant doorstep pickup for homes, clinics, labs and offices. Book online — starts ₹99/month.`
    : `${service.name} across Kerala by Rescigo. CPCB-compliant doorstep pickup with GPS-tracked vehicles, colour-coded segregation and annual compliance certificate.`;
  const keywords = `${service.metaKeywords}${citySlug ? `, ${service.short} ${cityLabel}${city.aliases ? `, ${service.short} ${city.aliases}` : ""}` : ""}, ${service.short} Kerala, ${service.short} India`;

  // JSON-LD stack — everything Google needs to place us in local + service KG.
  const jsonLd = [organizationSchema()];
  if (city.branch) jsonLd.push(localBusinessSchema(city.branch));
  jsonLd.push(serviceSchema({
    name: `${service.name}${citySlug ? ` in ${cityLabel}` : ""}`,
    description,
    path: pathBase,
    areaServed: citySlug ? [cityLabel, city.region, "India"] : ["Kerala", "India"],
  }));
  jsonLd.push(faqSchema(service.faqs));

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: service.name, path: `/services/${serviceSlug}` },
  ];
  if (citySlug) breadcrumbs.push({ name: cityLabel, path: pathBase });

  const ServiceIcon = service.icon;
  const otherCities = Object.keys(CITIES).filter(c => c !== (citySlug || "kerala") && c !== "india");

  return (
    <div className="min-h-screen bg-white" data-testid="page-service-landing">
      <SEO title={title} description={description} path={pathBase}
            keywords={keywords} jsonLd={jsonLd} breadcrumbs={breadcrumbs}/>
      <MarketingNav/>

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div aria-hidden className="absolute inset-0">
          <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl"
                style={{ background: "radial-gradient(circle, #2D5A27 0%, transparent 60%)" }}/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <nav className="mb-4 text-xs text-slate-500" aria-label="Breadcrumb">
            {breadcrumbs.map((b, i) => (
              <span key={i}>
                {i > 0 && " › "}
                <Link to={b.path} className="hover:text-slate-900">{b.name}</Link>
              </span>
            ))}
          </nav>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>{citySlug ? `Kerala · ${cityLabel}` : "Kerala & India"}</Eyebrow>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-4 text-slate-900 leading-[1.05] max-w-4xl">
              {service.heroTitle}
              {citySlug && <> in <span className="italic text-[#2D5A27]">{cityLabel}</span></>}
            </h1>
            <p className="text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">
              {city.intro} {citySlug ? "" : service.tagline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="landing-cta-primary" className={BTN_GREEN}>
                Book service <ArrowRight size={14} weight="bold"/>
              </Link>
              <a href="https://wa.me/917025967117" target="_blank" rel="noreferrer"
                  data-testid="landing-cta-whatsapp"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full border-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 text-sm font-medium">
                <WhatsappLogo size={16} weight="fill"/> WhatsApp us
              </a>
              <a href="tel:+917025967117" data-testid="landing-cta-call"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-800">
                <Phone size={14} weight="bold"/> +91-70259-67117
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} color="#2D5A27"/> CPCB Authorised</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={12} color="#0B2B66"/> Weekly / on-demand</span>
              <span className="inline-flex items-center gap-1.5"><MapPin size={12} color="#7A2E18"/> {cityLabel} + 12 cities</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service intro */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <div className="w-14 h-14 rounded-xl bg-[#2D5A27] text-white grid place-items-center mb-3">
              <ServiceIcon size={22} weight="duotone"/>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Service</div>
            <div className="font-heading text-2xl mt-1 text-slate-900">{service.name}</div>
            <div className="text-sm text-[#2D5A27] mt-2 font-medium">{service.price}</div>
          </div>
          <div className="md:col-span-2 text-slate-600 leading-relaxed">
            <p>{service.intro}</p>
            <ul className="mt-5 space-y-2">
              {service.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle size={16} weight="fill" color="#2D5A27" className="flex-none mt-0.5"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* City coverage grid */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <Eyebrow>Coverage</Eyebrow>
          <h2 className="font-heading text-2xl md:text-3xl mt-3 text-slate-900">
            {service.name} — served across Kerala
          </h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
            {otherCities.map(slug => (
              <Link key={slug} to={`/services/${serviceSlug}/${slug}`}
                     className={`p-4 rounded-xl border transition-all ${(slug === (citySlug || "kerala")) ? "border-[#2D5A27] bg-[#F4F8F2]" : "border-slate-200 hover:border-[#2D5A27] hover:shadow"}`}>
                <div className="text-sm font-medium text-slate-900">{CITIES[slug].name}</div>
                {CITIES[slug].aliases && (
                  <div className="text-[11px] text-slate-500 mt-0.5">{CITIES[slug].aliases}</div>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Eyebrow>Frequently asked</Eyebrow>
          <h2 className="font-heading text-2xl md:text-3xl mt-3 text-slate-900">
            Everything you'd ask about {service.name.toLowerCase()}
          </h2>
          <dl className="mt-8 space-y-6">
            {service.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                <dt className="font-heading text-base text-slate-900">{f.q}</dt>
                <dd className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Related services */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <Eyebrow>Related services</Eyebrow>
          <div className="mt-6 grid md:grid-cols-3 gap-3">
            {Object.entries(SERVICES).filter(([k]) => k !== serviceSlug).slice(0, 3).map(([slug, s]) => (
              <Link key={slug} to={`/services/${slug}${citySlug ? `/${citySlug}` : ""}`}
                     className="p-5 rounded-xl border border-slate-200 hover:border-[#2D5A27] hover:shadow transition-all">
                <div className="font-heading text-base text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500 mt-1">{s.tagline}</div>
                <div className="text-xs text-[#2D5A27] mt-3 inline-flex items-center gap-1">
                  Explore <ArrowRight size={10} weight="bold"/>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}

// ============================================================
// Services hub — /services (index page)
// Redesigned 2026-02 per the uploaded "Everyday care. Made simpler."
// theme: clean white canvas, coral-red primary CTA, pastel category
// tiles, hero family photo with floating service cards, and three
// pillar cards at the bottom (Health · Hygiene · BMW). Mobile-first.
// ============================================================
import { useNavigate } from "react-router-dom";
import {
  Pill, TestTube, Ambulance, Wheelchair, UserCircle, FirstAid,
  SprayBottle, Trash, MagnifyingGlass, MapPin as MapPinIcon,
  Star, CaretRight,
} from "@phosphor-icons/react";

const CITY_OPTIONS = [
  "Thiruvananthapuram", "Kochi", "Kozhikode", "Kottayam", "Thrissur",
];

// Each category tile mirrors the uploaded theme — soft pastel background,
// clear line icon, two-line title. "to" routes to the right surface
// when logged out (marketing landings) or in-app (customer flows).
const CATEGORY_TILES = [
  { key: "medicine",  title: "Medicine\nDelivery",      Icon: Pill,
    bg: "bg-[#FDE2E4]",       fg: "text-[#D23247]",       to: "/me/health/medicines" },
  { key: "labs",      title: "Lab Tests\n& Diagnostics", Icon: TestTube,
    bg: "bg-[#E1EEFF]",       fg: "text-[#2563EB]",       to: "/me/health/lab-tests" },
  { key: "ambulance", title: "Ambulance\nBooking",      Icon: Ambulance,
    bg: "bg-[#DCFCE7]",       fg: "text-[#15803D]",       to: "/me/health/ambulance" },
  { key: "equipment", title: "Medical\nEquipment",      Icon: Wheelchair,
    bg: "bg-[#EDE4FF]",       fg: "text-[#6B38CC]",       to: "/me/health/equipment" },
  { key: "doctor",    title: "Doctor\nConsultation",    Icon: UserCircle,
    bg: "bg-[#FEE4EC]",       fg: "text-[#D23247]",       to: "/me/health/doctor" },
  { key: "nursing",   title: "Home Nursing\n& Patient Care", Icon: FirstAid,
    bg: "bg-[#FFE4D1]",       fg: "text-[#D97706]",       to: "/me/health/home-care" },
  { key: "hygiene",   title: "Hygiene\nServices",       Icon: SprayBottle,
    bg: "bg-[#E1EEFF]",       fg: "text-[#2563EB]",       to: "/hygiene" },
  { key: "bmw",       title: "BMW\nCollection",         Icon: Trash,
    bg: "bg-[#FDE2E4]",       fg: "text-[#D23247]",       to: "/services/biomedical-waste-collection" },
];

const PILLARS = [
  {
    key: "health",
    title: "Health Services",
    body: "From medicines to diagnostics, doctors and home care — complete healthcare at your doorstep.",
    tint: "bg-[#FEE4EC]",
    cta: "Explore Health Services",
    to:  "/health",
    bullets: ["Verified partners", "Quality assured", "Convenient booking"],
    bulletColor: "text-[#D23247]",
    photo: "https://images.unsplash.com/photo-1612531386530-97286d97c2d2?auto=format&fit=crop&w=800&q=70",
    photoAlt: "Doctor smiling at the camera",
  },
  {
    key: "hygiene",
    title: "Hygiene Services",
    body: "Professional cleaning, disinfection and hygiene solutions for homes, offices and healthcare facilities.",
    tint: "bg-[#DCECFF]",
    cta: "Explore Hygiene Services",
    to:  "/hygiene",
    bullets: ["Trained professionals", "Safe products", "Flexible booking"],
    bulletColor: "text-[#2563EB]",
    photo: "https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&w=800&q=70",
    photoAlt: "Hygiene professional cleaning a surface",
  },
  {
    key: "bmw",
    title: "Biomedical Waste Management",
    body: "Doorstep collection, QR tracking and compliant disposal for homes, clinics and institutions.",
    tint: "bg-[#FEE4EC]",
    cta: "Explore BMW Services",
    to:  "/services/biomedical-waste-collection",
    bullets: ["QR tracked", "Compliant disposal", "For homes & institutions"],
    bulletColor: "text-[#15803D]",
    photo: "https://images.unsplash.com/photo-1583912086096-8c60d75a53f9?auto=format&fit=crop&w=800&q=70",
    photoAlt: "Biomedical waste collector in red uniform",
  },
];

function CategoryTile({ tile }) {
  const nav = useNavigate();
  const [first, second] = tile.title.split("\n");
  const Icon = tile.Icon;
  return (
    <button
      type="button"
      onClick={() => nav(tile.to)}
      data-testid={`services-cat-${tile.key}`}
      className="group text-left bg-white rounded-2xl border border-[#F0EEEA]
                 hover:border-[#D23247] hover:shadow-md transition-all
                 p-3 sm:p-4 flex items-center gap-3"
    >
      <div className={`${tile.bg} ${tile.fg} shrink-0 rounded-xl
                       w-11 h-11 sm:w-12 sm:h-12 grid place-items-center
                       transition-transform group-hover:scale-105`}>
        <Icon size={22} weight="duotone"/>
      </div>
      <div className="min-w-0">
        <div className="text-sm sm:text-[15px] font-semibold text-slate-900 leading-tight">
          {first}
        </div>
        <div className="text-sm sm:text-[15px] font-semibold text-slate-900 leading-tight">
          {second}
        </div>
      </div>
    </button>
  );
}

function PillarCard({ p }) {
  return (
    <article
      data-testid={`services-pillar-${p.key}`}
      className="rounded-3xl overflow-hidden bg-white border border-[#F0EEEA]
                 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className={`relative ${p.tint} p-5 sm:p-6 flex gap-4 min-h-[220px]`}>
        <div className="flex-1 min-w-0 pr-2 flex flex-col">
          <h3 className="font-heading text-2xl sm:text-[26px] text-slate-900 leading-tight">
            {p.title}
          </h3>
          <p className="text-[13px] sm:text-sm text-slate-700 mt-2 leading-relaxed max-w-xs">
            {p.body}
          </p>
          <Link
            to={p.to}
            data-testid={`services-pillar-${p.key}-cta`}
            className="mt-auto inline-flex items-center gap-2 h-11 px-4 rounded-full
                       bg-[#D23247] text-white text-xs sm:text-sm font-medium
                       hover:bg-[#B52236] transition-colors w-fit"
          >
            {p.cta} <ArrowRight size={14} weight="bold"/>
          </Link>
        </div>
        <div className="shrink-0 w-[42%] sm:w-[46%] relative">
          <img src={p.photo} alt={p.photoAlt}
                 loading="lazy"
                 className="absolute inset-0 w-full h-full object-cover rounded-2xl"/>
        </div>
      </div>
      <div className="px-5 sm:px-6 py-3 bg-white border-t border-[#F5F3EE]
                       flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px] sm:text-[13px]">
        {p.bullets.map((b, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-slate-700">
            <CheckCircle size={14} weight="fill" className={p.bulletColor}/> {b}
          </span>
        ))}
      </div>
    </article>
  );
}

export function ServicesHub() {
  const nav = useNavigate();
  const [city, setCity]   = React.useState(CITY_OPTIONS[0]);
  const [q,    setQ]      = React.useState("");
  const [cms,  setCms]    = React.useState(null);

  // Load CMS-managed images / logos / city chips / auth URLs.
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/services-page`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setCms(d); })
      .catch(() => { /* fall back to defaults */ });
    return () => { cancelled = true; };
  }, []);

  const C = cms || {};
  const heroImg  = C.hero_image_url  || "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&w=1200&q=70";
  const heroAlt  = C.hero_image_alt  || "Doctor consulting with a family at home";
  const pHealth  = C.pillar_health_image_url  || "https://images.unsplash.com/photo-1612531386530-97286d97c2d2?auto=format&fit=crop&w=800&q=70";
  const pHygiene = C.pillar_hygiene_image_url || "https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&w=800&q=70";
  const pBmw     = C.pillar_bmw_image_url     || "https://images.unsplash.com/photo-1583912086096-8c60d75a53f9?auto=format&fit=crop&w=800&q=70";
  const signupUrl = C.signup_url || "/me/login";
  const loginUrl  = C.login_url  || "/me/login";
  const partnerLogos = Array.isArray(C.partner_logos) ? C.partner_logos : [];
  const cityChips    = Array.isArray(C.cities) && C.cities.length
    ? C.cities
    : [
        { name: "Thiruvananthapuram", slug: "trivandrum" },
        { name: "Kochi", slug: "kochi" },
        { name: "Kottayam", slug: "kottayam" },
      ];

  const pillarsRuntime = [
    { ...PILLARS[0], photo: pHealth  },
    { ...PILLARS[1], photo: pHygiene },
    { ...PILLARS[2], photo: pBmw     },
  ];

  const doSearch = (e) => {
    e?.preventDefault?.();
    if (!q.trim()) {
      nav("/me/health/medicines");
      return;
    }
    nav(`/me/health/medicines?q=${encodeURIComponent(q.trim())}&city=${encodeURIComponent(city)}`);
  };

  const jsonLd = [
    organizationSchema(),
    ...CITY_BRANCHES.map(localBusinessSchema),
  ];
  const breadcrumbs = [
    { name: "Home",     path: "/" },
    { name: "Services", path: "/services" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="page-services-hub">
      <SEO
        title="Everyday care, made simpler — Rescigo Health"
        description="Medicines, lab tests, doctors, ambulances, home nursing, hygiene services and compliant biomedical waste disposal — one platform across Kerala."
        path="/services"
        keywords="medicine delivery, lab tests, ambulance booking, home nursing, hygiene services, biomedical waste Kerala"
        jsonLd={jsonLd}
        breadcrumbs={breadcrumbs}
      />
      <MarketingNav/>

      {/* ============================================================
          HERO · split copy + family photo + category grid
          ============================================================ */}
      <section className="pt-24 sm:pt-28 pb-10 sm:pb-16 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-14 items-start">
          {/* ---- Left: copy + search + categories ---- */}
          <div className="order-1">
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
              Care · Hygiene · Responsible Healthcare
            </div>
            <h1 className="font-heading text-[40px] sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-slate-900">
              Everyday care.<br/>
              <span className="text-[#D23247]">Made simpler.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-4 max-w-md">
              Healthcare, hygiene and essential care services, connected through one platform.
            </p>

            {/* Sign up / Login row (mobile-first stacked, inline on >=sm) */}
            <div className="mt-5 flex flex-wrap items-center gap-3"
                  data-testid="services-auth-row">
              <Link
                to={signupUrl}
                data-testid="services-signup"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full
                            bg-[#D23247] hover:bg-[#B52236] text-white
                            text-sm font-medium transition-colors"
              >
                Sign up now <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link
                to={loginUrl}
                data-testid="services-login"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full
                            border border-slate-300 bg-white hover:border-[#D23247]
                            hover:text-[#D23247] text-slate-800 text-sm font-medium
                            transition-colors"
              >
                Login now
              </Link>
              <span className="text-xs text-slate-500 pl-1">
                Free · takes 30 seconds
              </span>
            </div>

            {/* City chips — deep-link to city-scoped service landings */}
            <div className="mt-5 flex items-center gap-2 flex-wrap"
                  data-testid="services-city-chips">
              <span className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-medium">
                Serving
              </span>
              {cityChips.map(c => (
                <Link
                  key={c.slug}
                  to={`/services/biomedical-waste-collection/${c.slug}`}
                  data-testid={`services-city-chip-${c.slug}`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full
                              border border-[#F0EEEA] bg-white
                              hover:border-[#D23247] hover:text-[#D23247]
                              text-xs font-medium text-slate-700 transition-colors"
                >
                  <MapPinIcon size={12} weight="duotone" className="text-[#D23247]"/>
                  {c.name}
                </Link>
              ))}
            </div>

            {/* Search row */}
            <form
              onSubmit={doSearch}
              data-testid="services-search"
              className="mt-5 rounded-2xl bg-white border border-[#F0EEEA] shadow-sm
                         p-2 flex flex-wrap items-center gap-2"
            >
              <label className="flex items-center gap-2 px-3 h-11 rounded-full bg-[#FDE2E4]/50
                                shrink-0 cursor-pointer">
                <MapPinIcon size={16} weight="duotone" className="text-[#D23247]"/>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid="services-city"
                  className="bg-transparent text-sm font-medium text-slate-800 outline-none pr-1"
                >
                  {CITY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <div className="flex items-center gap-2 flex-1 min-w-[140px] px-3 h-11">
                <MagnifyingGlass size={16} className="text-slate-400 shrink-0"/>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search medicines, lab tests, home care and more"
                  data-testid="services-search-input"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                />
              </div>
              <button
                type="submit"
                data-testid="services-search-submit"
                className="h-11 px-5 sm:px-7 rounded-full bg-[#D23247] hover:bg-[#B52236]
                           text-white text-sm font-medium transition-colors w-full sm:w-auto"
              >
                Search
              </button>
            </form>

            {/* Category grid — 2 cols mobile, 4 cols desktop (as per theme) */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {CATEGORY_TILES.map(t => <CategoryTile key={t.key} tile={t}/>)}
            </div>
          </div>

          {/* ---- Right: family hero + floating service cards ---- */}
          <div className="order-2 relative">
            <div className="relative rounded-[32px] overflow-hidden
                             aspect-[4/5] sm:aspect-[5/6] lg:aspect-[6/7] bg-[#FDE2E4]">
              <img
                src={heroImg}
                alt={heroAlt}
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Floating card: Medicine Delivery */}
            <div className="hidden sm:flex absolute top-4 sm:top-6 -left-2 sm:left-6
                             bg-white rounded-2xl shadow-xl border border-[#F0EEEA]
                             p-3 pr-5 items-center gap-3 max-w-[220px]">
              <div className="w-10 h-10 rounded-xl bg-[#FDE2E4] text-[#D23247]
                               grid place-items-center shrink-0">
                <Pill size={20} weight="duotone"/>
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 leading-tight">
                  Medicine Delivery
                </div>
                <div className="text-[11px] text-slate-500 leading-tight">
                  From nearby pharmacies
                </div>
                <div className="text-[11px] text-[#15803D] font-semibold mt-0.5">
                  30-60 mins
                </div>
              </div>
            </div>

            {/* Floating card: Lab Tests */}
            <div className="hidden sm:flex absolute top-4 sm:top-10 -right-2 sm:right-4
                             bg-white rounded-2xl shadow-xl border border-[#F0EEEA]
                             p-3 pr-5 items-center gap-3 max-w-[200px]">
              <div className="w-10 h-10 rounded-xl bg-[#E1EEFF] text-[#2563EB]
                               grid place-items-center shrink-0">
                <TestTube size={20} weight="duotone"/>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-900 leading-tight">
                  Lab Tests
                </div>
                <div className="text-[11px] text-slate-500 leading-tight">
                  Home sample collection
                </div>
                <Link to="/me/health/lab-tests"
                       className="text-[11px] text-[#2563EB] font-semibold mt-0.5 inline-flex items-center gap-1">
                  Book Now <CaretRight size={10} weight="bold"/>
                </Link>
              </div>
            </div>

            {/* Floating card: Doctor Consultation */}
            <div className="hidden md:flex absolute top-[42%] -right-4 md:right-2
                             bg-white rounded-2xl shadow-xl border border-[#F0EEEA]
                             p-3 pr-5 items-center gap-3 max-w-[220px]">
              <div className="w-10 h-10 rounded-xl bg-[#FEE4EC] text-[#D23247]
                               grid place-items-center shrink-0">
                <UserCircle size={20} weight="duotone"/>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-900 leading-tight">
                  Doctor Consultation
                </div>
                <div className="text-[11px] text-slate-500 leading-tight">
                  Online or at home
                </div>
                <Link to="/me/health/doctor"
                       className="text-[11px] text-[#D23247] font-semibold mt-0.5 inline-flex items-center gap-1">
                  Book Now <CaretRight size={10} weight="bold"/>
                </Link>
              </div>
            </div>

            {/* Floating card: Ambulance Booking */}
            <div className="hidden sm:flex absolute bottom-20 sm:bottom-16 -left-2 sm:left-10
                             bg-white rounded-2xl shadow-xl border border-[#F0EEEA]
                             p-3 pr-5 items-center gap-3 max-w-[240px]">
              <div className="w-10 h-10 rounded-xl bg-[#FDE2E4] text-[#D23247]
                               grid place-items-center shrink-0">
                <Ambulance size={20} weight="duotone"/>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-900 leading-tight">
                  Ambulance Booking
                </div>
                <div className="text-[11px] text-slate-500 leading-tight">
                  Emergency & scheduled
                </div>
                <Link to="/me/health/ambulance"
                       className="text-[11px] text-[#D23247] font-semibold mt-0.5 inline-flex items-center gap-1">
                  Book Now <CaretRight size={10} weight="bold"/>
                </Link>
              </div>
            </div>

            {/* Floating pill: Trusted by families */}
            <div className="hidden sm:flex absolute bottom-4 right-2 sm:right-6
                             bg-white rounded-full shadow-xl border border-[#F0EEEA]
                             py-2 px-3 items-center gap-2">
              <div className="flex -space-x-2">
                {["https://randomuser.me/api/portraits/women/22.jpg",
                   "https://randomuser.me/api/portraits/men/41.jpg",
                   "https://randomuser.me/api/portraits/women/68.jpg"].map((src, i) => (
                  <img key={i} src={src} alt=""
                        className="w-7 h-7 rounded-full border-2 border-white object-cover"/>
                ))}
              </div>
              <div className="text-[11px] text-slate-700 leading-tight">
                <div className="font-semibold">Trusted by families</div>
                <div className="text-slate-500">across Kerala</div>
              </div>
              <div className="flex items-center gap-0.5 text-[#F59E0B]">
                <Star size={12} weight="fill"/>
                <span className="text-[11px] font-semibold text-slate-800 ml-0.5">4.8/5</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PARTNER LOGOS STRIP (CMS-managed) — rendered only if any
          logos are configured in /admin/content/services-page.
          ============================================================ */}
      {partnerLogos.length > 0 && (
        <section className="pb-12 max-w-7xl mx-auto px-5 lg:px-10"
                  data-testid="services-partner-strip">
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium text-center">
            Trusted by hospitals, clinics & pharmacies across Kerala
          </div>
          <div className="mt-5 flex items-center justify-center gap-6 sm:gap-10 flex-wrap
                           grayscale opacity-80">
            {partnerLogos.map((p, i) => (
              <img
                key={i}
                src={p.logo_url}
                alt={p.alt || p.name}
                title={p.name}
                loading="lazy"
                className="h-9 sm:h-11 object-contain max-w-[140px]"
                data-testid={`services-partner-logo-${i}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* ============================================================
          PILLARS — Health / Hygiene / BMW
          ============================================================ */}
      <section className="pb-20 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillarsRuntime.map(p => <PillarCard key={p.key} p={p}/>)}
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}
